import React, { useEffect, useRef, useState } from "react";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { DatePicker, Form } from "antd";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import { JPInputError } from "@companion-core/shared/app/Components/Inputs/JPInputError";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import getValidation from "@companion-core/shared/app/Utils/validators";
import {
  dateToLocalizedString,
  getLocalizedDateFormat,
  isBetweenDates,
  localizedStringToDate,
} from "@companion-core/shared/app/Utils/datetime";
import { JPInputDateProps } from "./jpInputDate.model";
import "@companion-core/web/src/Assets/Styles/scss/input.scss";
import InputClearButton from "@companion-core/web/src/Components/InputClearButton";

/**
 * Primary UI component for user interaction
 * @return {Component} Input component
 */
export const JPInputDate: React.FC<JPInputDateProps> = ({
  name,
  required = false,
  value,
  minimumDate = "",
  maximumDate = "",
  label,
  icon,
  validators,
  testID,
  payloadPath = name,
  disabled = false,
  setFieldErrors,
  onValueChange,
  registerValidation,
  ...props
}: JPInputDateProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance(); // Get current context form instance
  const [error, setError] = useState<string>("");
  const [inputValue, setInputValue] = useState<Dayjs | null>(
    value ? localizedStringToDate(value) : null
  );
  const inputValueRef = useRef(inputValue); // Create a ref to store the latest value and avoid stale closure

  const [localizedFormat, setLocalizedFormat] = useState<string>("MM/DD/YYYY");

  const { locale: lang, antdLocale } = useI18nStore((state) => ({
    locale: state.locale,
    antdLocale: state.antdLocale,
  }));

  useEffect(() => {
    if (registerValidation) registerValidation(name, validateInput);
  }, []);

  useEffect(() => {
    inputValueRef.current = inputValue; // Update ref value
  }, [inputValue]);

  useEffect(() => {
    setLocalizedFormat(getLocalizedDateFormat());
  }, [lang]);

  const handleInputChange = (date: Dayjs | null) => {
    const dateString = date ? dateToLocalizedString(date) : "";
    setInputValue(date);

    if (form && payloadPath) {
      form.setFieldsValue({
        [payloadPath]: dateString,
      });
    }
    if (onValueChange) {
      onValueChange(dateString);
    }
  };

  const validateInput = () => {
    const inputValueString = inputValueRef.current
      ? dateToLocalizedString(inputValueRef.current)
      : "";
    let err = !inputValueString && required ? "common.form.input.error.required" : "";

    if (!err && validators?.length) {
      const { isValid, errorMessages } = getValidation(validators, inputValueString);
      if (!isValid) err = errorMessages[0];
    }
    setError(err);
    if (setFieldErrors) setFieldErrors(err ? [err] : []);
  };

  const inputId = `${name}-date`;
  const errorId = `${name}-error`;

  return (
    <div className="JPInput">
      {label && (
        <InputLabel label={label} icon={icon} htmlFor={inputId} id={`${name}-date-label`} />
      )}
      <DatePicker
        {...props}
        id={inputId}
        status={error?.length ? "error" : ""}
        data-testid={testID}
        locale={antdLocale?.DatePicker}
        name={name}
        picker={"date"}
        placeholder={!disabled ? t("common.form.input.placeholder.date") : ""}
        showTime={false}
        value={inputValue}
        disabledDate={(d) => isBetweenDates(d, minimumDate, maximumDate)}
        format={localizedFormat}
        suffixIcon={false}
        showNow={false}
        allowClear={{
          clearIcon: (
            <InputClearButton
              testID={testID}
              isInputSelect={true}
              clearInput={() => setError("")}
            />
          ),
        }}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={validateInput}
      />
      {error && <JPInputError inputLabel={label} testID={testID} error={error} id={errorId} />}
    </div>
  );
};
